import axios, { AxiosError } from 'axios'

import { useAuthStore } from '@/stores/authStore'

import { accountsPaths } from './api-paths'

function authError(status: number, code: string, message: string): AxiosError {
  const err = new AxiosError(message, code, undefined, undefined, {
    status,
    data: { code },
  } as never)
  return err
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/** Error code when the session changed (sign-out or another account) during a refresh. */
export const SESSION_CHANGED_CODE = 'SESSION_CHANGED'

let inFlight: { promise: Promise<void>; refreshToken: string | null } | null = null

function parseRefreshResponse(data: unknown): {
  access: string
  refresh?: string
  expiresIn?: number
} | null {
  const d = data as Record<string, unknown>
  const inner = (d?.data ?? d) as Record<string, unknown>
  const access = (inner.access_token ?? d.access_token) as string | undefined
  if (!access || typeof access !== 'string') {
    return null
  }
  const refresh = inner.refresh_token ?? d.refresh_token
  const expiresInRaw = inner.expires_in ?? d.expires_in
  const expiresIn =
    typeof expiresInRaw === 'number' && Number.isFinite(expiresInRaw) && expiresInRaw > 0
      ? expiresInRaw
      : undefined
  return {
    access,
    refresh: typeof refresh === 'string' ? refresh : undefined,
    expiresIn,
  }
}

const REFRESH_LOCK_NAME = 'maktab-token-refresh'

/**
 * Run `task` while holding a lock shared by every tab of this origin, so only one tab
 * exchanges the (single-use, rotated) refresh token at a time.
 */
function withCrossTabLock(task: () => Promise<void>): Promise<void> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  return locks ? locks.request(REFRESH_LOCK_NAME, task) : task()
}

/**
 * Refreshes the access token using the stored refresh token.
 * Single-flight: concurrent callers share one in-flight request, and tabs take turns.
 * A tab that waited while another tab rotated the tokens adopts them instead of
 * spending the already-used refresh token.
 * On success, updates the auth store (including rotated refresh token when present).
 * On failure, rethrows so the API client can map errors to logout + messaging.
 */
export function performTokenRefresh(): Promise<void> {
  const staleRefreshToken = useAuthStore.getState().refreshToken
  // A refresh started for another session (the user signed out or switched accounts since)
  // must not be shared with this one.
  if (inFlight && inFlight.refreshToken === staleRefreshToken) {
    return inFlight.promise
  }

  const entry: { promise: Promise<void>; refreshToken: string | null } = {
    promise: Promise.resolve(),
    refreshToken: staleRefreshToken,
  }
  entry.promise = withCrossTabLock(async () => {
    try {
      await useAuthStore.persist.rehydrate()
      const { refreshToken, accessToken } = useAuthStore.getState()
      if (refreshToken && accessToken && refreshToken !== staleRefreshToken) {
        return
      }
      if (!refreshToken) {
        throw authError(401, 'TOKEN_NOT_VALID', 'No refresh token')
      }

      const response = await axios.post(
        API_BASE_URL + accountsPaths.refreshToken,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )

      const parsed = parseRefreshResponse(response.data)
      if (!parsed) {
        throw authError(401, 'TOKEN_REFRESH_FAILED', 'Invalid refresh response')
      }

      // Only the session that started this refresh may receive the new tokens.
      if (useAuthStore.getState().refreshToken !== refreshToken) {
        throw authError(401, SESSION_CHANGED_CODE, 'Session changed during token refresh')
      }

      useAuthStore.getState().applyTokenRefresh({
        access: parsed.access,
        refresh: parsed.refresh,
        expiresIn: parsed.expiresIn,
      })
    } finally {
      if (inFlight === entry) inFlight = null
    }
  })
  inFlight = entry

  return entry.promise
}
